import { Button, Icon, Header, Modal, Select } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { RoleComponent } from 'components/roleComponent';
import { useTranslation } from 'react-i18next';
import { CompanyDTO } from 'models/userManager/addCustomer';
import { useEffect, useState } from 'react';
import { COMPANY_MANAGER, BSLBR, notificationAtom } from 'resources';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { bslbrCountriesAtom, bslbrPartnersAtom } from 'resources/atoms/bslbrAtom';
import useFetch from 'helpers/hooks/useFetch';
import { FilterHolidayDTO, HolidayDTO } from 'models/bslbr/general';
import AddHoliday from './new-holiday/addHoliday';
import useUser from 'helpers/hooks/useUser';

function HolidaySearch() {
    const { t } = useTranslation(['bslbr', 'common']);
    const InitialFilter = {
        Country: '',
        PageIndex: 1,
        PageSize: 10
    };
    const [filters, setFilters] = useState(InitialFilter);
    const [filteredData, setFilteredData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [showAddHoliday, setShowAddHoliday] = useState(false);
    const [countries, setCountries] = useRecoilState(bslbrCountriesAtom);
    const [partners, setPartners] = useRecoilState(bslbrPartnersAtom);
    const [modalDelete, setModalDelete] = useState(false);
    const [idHoliday, setIdHoliday] = useState<number>();
    const { user: userDetails } = useUser();
    const SUPERADMIN = '1';
    const setNotification = useSetRecoilState(notificationAtom);

    const [, getHoliday, loadingGetHoliday] = useFetch<FilterHolidayDTO, HolidayDTO[]>({
        url: BSLBR.getHolidays,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, holidayDelete, loadingDelete] = useFetch<{ id: number }, ConstrainBoolean>({
        url: `${BSLBR.deleteHoliday}`,
        method: 'DELETE',
        onError: t('common:errors.Unexpected')
    });

    const [, getPartners] = useFetch({
        url: COMPANY_MANAGER.getHslPartners,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        if (!partners.length) {
            getAllPartners();
        }
        getFilteredHoliday(InitialFilter);
    }, []);

    const resetFilters = () => {
        setFilters(InitialFilter);
        getFilteredHoliday(InitialFilter);
    };

    const getFilteredHoliday = async (filter: FilterHolidayDTO) => {
        await getHoliday(filter, (current: any) => {
            if (current.data) {
                setFilteredData(current.data?.Data);
                setTotalItems(current.data?.TotalCount);
            }
        });
    };

    const applyFilters = () => {
        getFilteredHoliday(filters);
    };

    const getAllPartners = () => {
        getPartners(null, resp => {
            if (resp.data) {
                const partnersData = resp.data.map((partner: CompanyDTO) => ({
                    value: partner.name,
                    text: partner.name
                }));
                setPartners(partnersData);

                const uniqueCountries = Array.from(new Set(resp.data.map((partner: CompanyDTO) => String(partner.country))));
                const countriesData = uniqueCountries.map(country => ({
                    value: country as string,
                    text: country as string
                }));
                setCountries(countriesData);
            }
        });
    };

    const deleteHoliday = async (id: number) => {
        try {
            await holidayDelete({ id });
            setModalDelete(false);
            getFilteredHoliday(filters);
            setNotification(old => ({ ...old, message: t('bslbr:translation.HolidaysDelete'), type: 'success' }));
        } catch {
            setNotification(old => ({ ...old, message: t('bslbr:translation.HolidaysDeleteError'), type: 'critical' }));
        }
    };

    const searchFieldsRender = () => {
        return (
            <div>
                <Header title={t('pages:holiday')} logo={false} menu={false}>
                    <div style={{ margin: '10px' }}>
                        <RoleComponent roles={['SUPERADMIN']}>
                            <Button
                                content={t('bslbr:translation.AddHoliday')}
                                type="primary"
                                size="medium"
                                iconPosition="right"
                                icon={<Icon name="add" root="building" />}
                                onClick={() => setShowAddHoliday(true)}
                                description={t('bslbr:translation.AddHoliday')}
                            />
                        </RoleComponent>
                    </div>
                </Header>
                <div className="form4Columns">
                    <Select
                        placeholder={t('user:address.Country')}
                        label={t('user:address.Country')}
                        options={countries}
                        search={true}
                        onChange={value => setFilters({ ...filters, Country: value })}
                        value={filters.Country}
                    />
                </div>
                <div className="buttonsSearch">
                    <Button
                        type="secondary"
                        icon="badge-delete"
                        content={t('common:actions.ResetFilters')}
                        iconPosition="right"
                        onClick={resetFilters}
                    />
                    <Button
                        type="primary"
                        icon="filter1"
                        content={t('common:actions.ApplyFilters')}
                        iconPosition="right"
                        onClick={applyFilters}
                    />
                </div>
            </div>
        );
    };

    const tableHolidayRender = () => {
        return (
            <div style={{ marginTop: '26px', marginLeft: '20px' }}>
                <DataTable
                    columnResizeMode="fit"
                    data={filteredData ?? []}
                    scrollable={true}
                    scrollWidth="100%"
                    resizableColumns={true}
                    loading={loadingGetHoliday}
                >
                    <DataTable.Column field={'Id'} align="center" header={t('bslbr:translation.Id')} sortable={true} />
                    <DataTable.Column field={'Country'} align="center" header={t('common:translation.Country')} sortable={true} />
                    <DataTable.Column field={'Data'} align="center" header={t('common:translation.Date')} sortable={true} />
                    <DataTable.Column
                        field={'Description'}
                        align="center"
                        header={t('common:translation.Description')}
                        sortable={true}
                    />
                    {userDetails?.role === SUPERADMIN ? (
                        <DataTable.Column
                            field="Delete"
                            renderer={e => (
                                <Icon
                                    size="small"
                                    root="common"
                                    name="delete"
                                    rotated={190}
                                    className="icon-event"
                                    disabled={userDetails?.role}
                                    onClick={() => {
                                        setIdHoliday(e.rowData.Id);
                                        setModalDelete(true);
                                    }}
                                />
                            )}
                            align="center"
                            header={t('common:actions.Delete')}
                        />
                    ) : (
                        []
                    )}
                </DataTable>
                <DataTable.Pagination
                    totalItems={totalItems}
                    itemsPerPage={10}
                    onPageChange={pageNumber => {
                        const updatedFilters = { ...filters, PageIndex: Number(pageNumber) };
                        getFilteredHoliday(updatedFilters);
                    }}
                />
            </div>
        );
    };

    const renderModalEliminateHoliday = () => {
        return (
            <Modal
                open={modalDelete}
                closeIcon={true}
                onClose={() => setModalDelete(false)}
                closeOnDimmerClick={true}
                size="small"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <h1>{t('bslbr:translation.Delete')}</h1>
                </Modal.Header>
                <Modal.Content>
                    <p>{t('bslbr:translation.ConfirmDeleteHoliday')}</p>
                </Modal.Content>

                <Modal.Footer>
                    <Button
                        type="primary"
                        content={t('common:translation.Confirm')}
                        onClick={() => {
                            deleteHoliday(idHoliday!);
                        }}
                        loading={loadingDelete}
                        disabled={loadingDelete}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    return (
        <>
            {showAddHoliday ? (
                <AddHoliday
                    onSave={() => setShowAddHoliday(false)}
                    onBack={() => {
                        setShowAddHoliday(false);
                        getFilteredHoliday(filters);
                    }}
                />
            ) : (
                <>
                    {searchFieldsRender()}
                    {tableHolidayRender()}
                    {idHoliday && renderModalEliminateHoliday()}
                </>
            )}
        </>
    );
}

export default HolidaySearch;
